import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, take, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { CLOSE_BUTTON, CONFIRM_BUTTON } from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { IFormKey, IFormSelectOption } from 'src/app/modules/shared/models/forms';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { BeneficiaryFormIsvComponent } from '../beneficiary-form-isv/beneficiary-form-isv.component';
import { address_enabling_map, address_form_keys, address_form_template, address_form_validators, contact_form_keys, contact_form_template, third_party_registry_form_keys, third_party_registry_form_template } from '../beneficiary-form-isv/beneficiary-form-isv.constants';
import { EModalOutput } from './../../../shared/components/basic-modal/basic-modal.constants';
import { EBeneficiaryTypeIsv, ILifeDeathBeneficiary, IPersonDataIsv } from './beneficiary-change-isv.constants';
import { ICityObject } from 'src/app/modules/shared/models/policy.model';

@Component({
  selector: 'app-beneficiary-change-isv',
  templateUrl: './beneficiary-change-isv.component.html',
  styleUrl: './beneficiary-change-isv.component.scss'
})
export class BeneficiaryChangeIsvComponent implements OnInit, OnDestroy {

  @Input() beneficiariesList: ILifeDeathBeneficiary[];
  @Input() thirdPartyContact: IPersonDataIsv;
  @Input() countriesList: IFormSelectOption[];
  @Input() provincesList: IFormSelectOption[];
  @Input() holderBeneficiaryRelationshipList: IFormSelectOption[];
  @Input() insuredBeneficiaryRelationshipList: IFormSelectOption[];
  @Input() beneficiaryDeathTipologies: IFormSelectOption[];
  @Input() beneficiaryLifeTipologies: IFormSelectOption[];
  @Input() isReadOnly: boolean;

  @Output() emitBeneficiariesList = new EventEmitter<ILifeDeathBeneficiary[]>();
  @Output() emitThirdPartyContact = new EventEmitter<{ data: IPersonDataIsv, isValid: boolean }>();

  beneficiaryTypes: any[];

  thirdPartyRegistryForm: FormGroup;
  thirdPartyRegistryFormKeys: IFormKey[];
  thirdPartyAddressForm: FormGroup;
  thirdPartyAddressFormKeys: IFormKey[];
  thirdPartyContactForm: FormGroup;
  thirdPartyContactFormKeys: IFormKey[];

  destroy$ = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _policyService: PolicyService,
    private readonly _spinnerService: SpinnerService,
    private readonly _alertService: AlertService

  ) { }

  ngOnInit(): void {
    this.initializeBeneficiaryTypes();
    this.manageForms();
    this.emitThirdPartyContact.emit({
      data: this.thirdPartyContact,
      isValid: this.isValidThirdParty
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  initializeBeneficiaryTypes(): void {
    this.beneficiaryTypes = [
      {
        title: 'Beneficiari Vita',
        list: this.beneficiariesList?.filter(beneficiary => beneficiary.beneficiaryType?.startsWith(EBeneficiaryTypeIsv.VITA)),
        type: EBeneficiaryTypeIsv.VITA,
        hidden: !this.canChangeLifeBeneficiaries
      },
      {
        title: 'Beneficiari Morte',
        list: this.beneficiariesList?.filter(beneficiary => beneficiary.beneficiaryType?.startsWith(EBeneficiaryTypeIsv.MORTE)),
        type: EBeneficiaryTypeIsv.MORTE,
        hidden: false
      }
    ];
  }

  manageForms(): void {
    const registryTemplate = this.thirdPartyContact ? 
      {
        name: this.thirdPartyContact?.firstName,
        surname: this.thirdPartyContact?.lastName,
        taxCode: this.thirdPartyContact?.fiscalCode
      } : third_party_registry_form_template;
    this.thirdPartyRegistryForm = FormUtils.initFormGroupFromTemplate(registryTemplate, /*third_party_registry_form_validators*/);
    const address = this.thirdPartyContact?.addresses[0];
    const addressTemplate = this.thirdPartyContact ? 
      {
        country: address?.nationCode,
        province: address?.provinceCode,
        place: address?.city,
        postalCode: address?.zipCode,
        address: address?.street
      } : address_form_template;
    this.thirdPartyAddressForm = FormUtils.initFormGroupFromTemplate(addressTemplate, /*address_form_validators*/);
    const contactTemplate = this.thirdPartyContact ? 
      {
        phone: this.thirdPartyContact?.contact?.phoneNumber,
        email: this.thirdPartyContact?.contact?.email
      } : contact_form_template;
    this.thirdPartyContactForm = FormUtils.initFormGroupFromTemplate(contactTemplate, /*contact_form_validators*/);

    this.thirdPartyRegistryFormKeys = CommonUtils.assign(third_party_registry_form_keys);
    this.thirdPartyAddressFormKeys = CommonUtils.assign(address_form_keys);
    this.thirdPartyContactFormKeys = CommonUtils.assign(contact_form_keys);

    if (this.thirdPartyAddressFormKeys) {
      const countryKey = this.thirdPartyAddressFormKeys.find(key => key.controlName === 'country');
      if (countryKey) {
        countryKey.options = this.countriesList;
      }
      if (this.thirdPartyContact) {
        const provinceKey = this.thirdPartyAddressFormKeys.find(key => key.controlName === 'province');
        if (provinceKey) {
          provinceKey.options = this.provincesList;
        }
        this.retrieveCities(this.thirdPartyAddressForm?.get('province').value);
      }
    }

    if (this.isReadOnly) {
      this.thirdPartyRegistryForm?.disable();
      this.thirdPartyAddressForm?.disable();
      this.thirdPartyContactForm?.disable();
    }

    this.thirdPartyRegistryForm?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onThirdPartyContactChange();
        this.emitThirdPartyContact.emit({
          data: this.thirdPartyContact,
          isValid: this.isValidThirdParty
        });
      })
    this.thirdPartyAddressForm?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onThirdPartyContactChange();
        this.emitThirdPartyContact.emit({
          data: this.thirdPartyContact,
          isValid: this.isValidThirdParty
        });
      })
    this.thirdPartyContactForm?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onThirdPartyContactChange();
        this.emitThirdPartyContact.emit({
          data: this.thirdPartyContact,
          isValid: this.isValidThirdParty
        });
      })

    this.setupAddressEnabling();
  }

  setupAddressEnabling(): void {
    Object.keys(address_enabling_map).forEach(controlName => {
      this.thirdPartyAddressForm?.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (controlName === 'country') {
            const formKey = this.thirdPartyAddressFormKeys?.find(key => key.controlName === 'province');
            if (formKey) {
              if (value === 'IT') {
                formKey.type = 'SELECT';
                formKey.options = this.provincesList;
              } else {
                formKey.options = null;
                formKey.type = 'INPUT'
              }
            }
          } else if (controlName === 'province' && value) {
            const formKey = this.thirdPartyAddressFormKeys?.find(key => key.controlName === 'place');
            if (this.thirdPartyAddressForm?.get('country').value === 'IT') {
              formKey.type = 'SELECT';
              this.retrieveCities(value);
            } else {
              if (formKey) {
                formKey.options = null;
                formKey.type = 'INPUT';
              }
            }
          }
          this.manageAddressFields(controlName, value);
        });
    });
  }

  manageAddressFields(controlName: string, value: string): void {
    const childControlName = address_enabling_map[controlName];
    const control = this.thirdPartyAddressForm.get(childControlName);
    if (value && this.thirdPartyAddressForm?.get(controlName)) {
      control.enable();
      control.reset();
      control.setValidators(address_form_validators[childControlName]);
      if (this.thirdPartyAddressForm?.get('country').value !== 'IT' && controlName === 'place') {
        this.thirdPartyAddressForm.get('postalCode').reset();
        this.thirdPartyAddressForm.get('postalCode').disable();
        this.thirdPartyAddressForm.get('postalCode').setValidators(null);
        this.thirdPartyAddressForm.get('address').enable();
      }
    } else {
      control.reset();
      control.disable();
    }
  }

  retrieveCities(value: string): void {
    this._spinnerService.show();
    this._policyService.retrieveCities(value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { listaComuni: ICityObject[] }) => {
          LoggerService.logInfo(this.constructor.name, 'retrieveCities', { response });
          if (response) {
            const formKey = this.thirdPartyAddressFormKeys?.find(key => key.controlName === 'place');
            if (formKey) {
              formKey.options = response.listaComuni?.map(obj => ({
                key: obj.cityName,
                value: obj.cityName,
              }))
            }
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrieveCities', { error });
          this._alertService.openErrorMessage('Errore nel recupero della lista delle città', 'retrieveCities', error);
          this._spinnerService.hide();
        }
      });
  }

  openNewBeneficiaryModal(beneficiaryType: EBeneficiaryTypeIsv, beneficiary?: ILifeDeathBeneficiary): void {
    LoggerService.logInfo(this.constructor.name, 'openNewBeneficiaryModal', beneficiaryType);
    this._dialog.open(BeneficiaryFormIsvComponent, {
      data: {
        beneficiaryType: beneficiaryType,
        countriesList: this.countriesList,
        provincesList: this.provincesList,
        holderBeneficiaryRelationshipList: this.holderBeneficiaryRelationshipList,
        insuredBeneficiaryRelationshipList: this.insuredBeneficiaryRelationshipList,
        beneficiaryDeathTipologies: this.beneficiaryDeathTipologies,
        beneficiaryLifeTipologies: this.beneficiaryLifeTipologies,
        canChangeLifeBeneficiaries: this.canChangeLifeBeneficiaries,
        beneficiary: beneficiary,
        isReadOnly: this.isReadOnly,
        buttons: [
          CLOSE_BUTTON,
          CONFIRM_BUTTON
        ]
      },
      minWidth: '70vw',
      minHeight: '55vh',
      disableClose: true
    })
    .afterClosed()
    .pipe(take(1))
    .subscribe(outcome => {
      if (outcome && outcome.command === EModalOutput.CONFIRM) {
        LoggerService.logInfo(this.constructor.name, 'openNewBeneficiaryModal', { outcome });
        if (beneficiary) {
          this.removeBeneficiary(beneficiary);
        }
        this.beneficiariesList.push(outcome.output);
        let beneficiaryType = this.beneficiaryTypes.find(type => type.type === outcome.output.beneficiaryType);
        if (beneficiaryType) {
          beneficiaryType.list.push(outcome.output);
          this.emitBeneficiariesList.emit(this.beneficiariesList);
        }
      }
    })
  }

  removeBeneficiary(beneficiary: ILifeDeathBeneficiary): void {
    const fiscalCode = beneficiary.beneficiaries?.[0]?.personData?.fiscalCode;
    const beneficiaryType = beneficiary.beneficiaryType;
    const filterFn = (item: ILifeDeathBeneficiary) => {
      if (item.beneficiaryType === beneficiaryType) {
        if (fiscalCode) {
          return item.beneficiaries?.[0]?.personData?.fiscalCode !== fiscalCode;
        } else {
          return item.description !== beneficiary.description;
        }
      }
      return true;
    };
    this.beneficiariesList = this.beneficiariesList.filter(filterFn);
    this.beneficiaryTypes.forEach(type => {
      type.list = type.list.filter(filterFn);
    });
    this.emitBeneficiariesList.emit(this.beneficiariesList);
    LoggerService.logInfo(this.constructor.name, 'removeBeneficiary', { beneficiary });
  }

  modifyBeneficiary(beneficiary: ILifeDeathBeneficiary): void {
    LoggerService.logInfo(this.constructor.name, 'modifyBeneficiary', beneficiary);
    this.openNewBeneficiaryModal(beneficiary.beneficiaryType, beneficiary)
  }

  onThirdPartyContactChange(): void {
    const registryRawValue = this.thirdPartyRegistryForm?.getRawValue();
    const addressRawValue = this.thirdPartyAddressForm?.getRawValue();
    this.thirdPartyContact = {
      fiscalCode: registryRawValue?.taxCode,
      personType: 'Fisica',
      lastName: registryRawValue?.surname,
      firstName: registryRawValue?.name,
      addresses: [
        {
          nationCode: addressRawValue?.country,
          provinceCode: addressRawValue?.province,
          city: addressRawValue?.place,
          nation: addressRawValue?.country,
          province: addressRawValue?.province,
          addressType: "Contatto",
          zipCode: addressRawValue?.postalCode,
          street: addressRawValue?.address,
        }
      ],
      contact: {
        phoneNumber: this.thirdPartyContactForm?.get('phone')?.value,
        email: this.thirdPartyContactForm?.get('email')?.value
      }
    }
  }
  canViewBeneficiary(beneficiary: ILifeDeathBeneficiary): boolean {
    return beneficiary?.beneficiaries?.length > 0;
  }

  getBeneficiaryDescription(beneficiary: ILifeDeathBeneficiary): string {
    let description: string = beneficiary?.beneficiary ? beneficiary?.beneficiary : beneficiary?.description;
    if (description === 'Beneficiari nominativi') {
      const beneficiaries = beneficiary?.beneficiaries;
      const personData: IPersonDataIsv = beneficiaries?.length > 0 ? beneficiaries[0]?.personData : null;
      if (personData) {
        if (personData.personType === 'Fisica') {
          description += ' - ' + personData.firstName + ' ' + personData.lastName + ' - ' + personData.fiscalCode;
        } else if (personData.personType === 'Giuridica') {
          description += ' - ' + personData.businessName + ' - ' + personData.vatCode;
        }
      }
    }
    return description;
  }

  get canChangeLifeBeneficiaries(): boolean {
    return this.beneficiariesList?.find(beneficiary => beneficiary.beneficiaryType?.startsWith(EBeneficiaryTypeIsv.VITA)) !== undefined;
  }

  get isValidThirdParty(): boolean {
    return this.thirdPartyRegistryForm?.valid &&
      this.thirdPartyAddressForm?.valid &&
      this.thirdPartyContactForm?.valid;
  }

}
